import React, { useMemo } from 'react';
import cx from 'classnames';
import { useParams, Link } from 'react-router-dom';
import { Card, CardBody } from 'reactstrap';

import styles from './ProductManager.module.scss';
import { Icon, IconStack } from 'js/components';
import {
  faUser,
  faCircleSmall,
  faStar,
  faHeadSideGear,
} from '@fortawesome/pro-solid-svg-icons';
import {
  faCalendarAlt,
  faStar as farStar,
} from '@fortawesome/pro-regular-svg-icons';
import { useStandardCollator } from 'js/utils/string';
import { format } from 'date-fns';

function UserGroupStatCard({ userGroup, isFavorited, isPmFor }) {
  const { workspaceId } = useParams();

  const collator = useStandardCollator();
  const capabilities = useMemo(() => {
    return (userGroup?.capability_sets ?? [])
      .flatMap((cs) => cs.capabilities)
      .sort(
        (a, b) =>
          collator.compare(a.name, b.name) || collator.compare(a.id, b.id)
      );
  }, [userGroup, collator]);

  const lastAssessedLabel = useMemo(() => {
    return userGroup.last_assessed_at
      ? format(new Date(userGroup.last_assessed_at), 'MMM d, yyyy')
      : 'Never';
  }, [userGroup]);

  const reviewAssessmentPath =
    userGroup.most_recent_assessment_id &&
    `/w/${workspaceId}/pm/user-groups/${userGroup.id}/assessments/${userGroup.most_recent_assessment_id}/review`;

  return (
    <Card
      className={cx(styles.card, {
        [styles.favorite]: isFavorited,
        [styles.managed]: isPmFor,
      })}
    >
      <CardBody>
        <div className={styles.badges}>
          {isFavorited && (
            <div>
              <IconStack className="icon-left">
                <Icon icon={faStar} className={cx(styles['favorite-icon'])} />
                <Icon
                  icon={farStar}
                  className={cx(styles['favorite-icon-outline'])}
                />
              </IconStack>
              <span className={styles['badge-text']}>Favorite</span>
            </div>
          )}
          {isPmFor && (
            <div title="You are a registered product manager for this user group">
              <Icon
                icon={faHeadSideGear}
                className={cx(styles['pm-icon'], 'icon-left')}
              />
              <span className={styles['badge-text']}>Managed</span>
            </div>
          )}
        </div>
        <div
          className={cx(styles['stat-card-body'], { favorite: isFavorited })}
        >
          <div className={styles['stat-card-main']}>
            <div className={styles['stat-card-main-header']}>
              <h5>
                <Link to={`/w/${workspaceId}/pm/user-groups/${userGroup.id}`}>
                  {userGroup.name}
                </Link>
              </h5>
              <div className={styles['stat-card-product-managers']}>
                {(userGroup.product_managers ?? []).map((pm) => {
                  return <span key={pm.id}>{pm.name}</span>;
                })}
              </div>
            </div>
            {capabilities.length > 0 ? (
              <div className={styles['stat-card-capabilities']}>
                {capabilities.map((c, i) => {
                  return (
                    <React.Fragment key={c.id}>
                      {i > 0 && (
                        <Icon
                          icon={faCircleSmall}
                          className="text-muted icon-left icon-right"
                          transform="shrink-9"
                        />
                      )}
                      <span>{c.name}</span>
                    </React.Fragment>
                  );
                })}
              </div>
            ) : (
              <div className={styles['stat-card-capabilities-empty']}>
                <span>No assessed capabilities</span>
              </div>
            )}
          </div>
          <div className={styles['stat-card-sidebar']}>
            <div className={styles['stat-card-members']}>
              <Icon icon={faUser} className="text-info icon-left" />
              <span>{(userGroup.members ?? []).length}</span>
            </div>
            <div className={styles['stat-card-last-assessed']}>
              <label>Last assessed</label>
              <div>
                <Icon icon={faCalendarAlt} className="text-info icon-left" />
                {reviewAssessmentPath ? (
                  <Link to={reviewAssessmentPath}>{lastAssessedLabel}</Link>
                ) : (
                  <span>{lastAssessedLabel}</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

export default UserGroupStatCard;
